import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useTheme } from "@/contexts/ThemeContext";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import logoImg from "@/assets/logo.png";
import logoDarkImg from "@/assets/logo-dark.png";
import logoNavbarSm from "@/assets/logo-navbar-sm.webp";

interface DynamicLogoProps {
  className?: string;
  imgClassName?: string;
  showText?: boolean;
  textClassName?: string;
  collapsed?: boolean;
  type?: "public" | "dashboard";
  width?: number;
  height?: number;
}

export function DynamicLogo({
  className,
  imgClassName,
  showText = false,
  textClassName,
  collapsed = false,
  type = "public",
  width = 40,
  height = 40,
}: DynamicLogoProps) {
  const { theme } = useTheme();
  const [imgError, setImgError] = useState(false);

  const { data: companySettings, isLoading } = useQuery({
    queryKey: ["company-settings-public"],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_public_company_settings");
      if (error) {
        console.error("Error fetching company settings:", error);
        return null;
      }
      return data as Record<string, string> | null;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const companyLogoUrl = companySettings?.logoUrl;
  const genericFallback = "/favicon.ico";

  const localFallback = useMemo(() => {
    if (type === "public") {
      return theme === "dark" ? logoDarkImg : logoNavbarSm;
    }
    return theme === "dark" ? logoDarkImg : logoImg;
  }, [theme, type]);

  const currentLogo = useMemo(() => {
    if (imgError) {
      return genericFallback;
    }
    if (!companyLogoUrl) {
      return localFallback;
    }
    return companyLogoUrl;
  }, [companyLogoUrl, localFallback, imgError]);

  if (isLoading) {
    return (
      <div className={cn("flex items-center gap-2", className)}>
        <Skeleton 
          className={cn("rounded-lg animate-pulse", imgClassName)} 
          style={{ width: `${width}px`, height: `${height}px` }} 
        />
        {showText && !collapsed && (
          <Skeleton className="h-6 w-32" />
        )}
      </div>
    );
  }

  return (
    <div className={cn("flex items-center gap-2.5 group", className)}>
      <div className="relative shrink-0">
        <img
          src={currentLogo}
          alt="SCOOTERP - Software ERP para Patinetes"
          className={cn(
            "object-contain transition-all duration-300",
            !collapsed && "group-hover:scale-110",
            imgClassName
          )}
          style={{ width: `${width}px`, height: `${height}px` }}
          width={width}
          height={height}
          fetchPriority="high"
          loading="eager"
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            console.warn(`DynamicLogo: Failed to load ${target.src}`);
            if (target.src !== localFallback && !imgError) {
              target.src = localFallback;
            } else if (target.src !== genericFallback) {
              target.src = genericFallback;
              setImgError(true);
            }
          }}
        />
        <div className="absolute inset-0 rounded-full bg-primary/20 blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
      </div>

      {showText && (
        <div className={cn(
          "flex flex-col transition-all duration-300",
          collapsed ? "w-0 opacity-0 overflow-hidden" : "w-auto opacity-100",
          textClassName
        )}>
          <span className="text-lg font-bold tracking-tight bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent group-hover:from-primary group-hover:to-accent transition-all duration-500">
            {companySettings?.companyName || "SCOOTERP"}
          </span>
          {type === "dashboard" && (
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">
              Portal de Gestión
            </span>
          )}
        </div>
      )}
    </div>
  );
}
